/**
 * @file        src/lib/tariffs/recyclingFee.ts
 * @description Утилизационный сбор категории M1 по проверенному справочнику ПП 1291 (раздел I, в ред.
 *              ПП 1713): выбор пункта (1–4), блока объёма и редакции по дате, коэффициент по точной
 *              мощности в кВт — плюс текст строки разбивки. Вход — уже готовая мощность в кВт: для
 *              ДВС это мощность двигателя, для гибрида с известными мощностями ДВС и мотора — их сумма
 *              «ДВС + 30-мин мотора», для электромобиля и последовательного гибрида — 30-минутная
 *              (обе выбирает thirtyMinutePower.ts).
 * @rule        Коэффициенты только из справочника (findCoefficientKw). Ни таблиц, ни порогов в л.с.,
 *              ни округления мощности здесь нет: «округление в меньшую сторону не допускается».
 * @rule        Пункт выбирается так: личное пользование физлица → п.3 (EV) / п.4 (ДВС); всё остальное
 *              (юрлицо, ИП, физлицо не для себя) → п.1 / п.2 — это ОДНА коммерческая таблица.
 * @rule        Льгота не проверяется отдельным условием: она уже записана в п.3/п.4 справочника
 *              (0,17/0,26 только ≤58,84 кВт у EV и ≤117,68 кВт при объёме ≤3000 см³ у ДВС).
 * @universal   knowledge/universals/engines.md#customs-calculator-engine
 */

import {
  findCoefficientKw,
  resolveRevision,
  volumeBlockForCc,
  RECYCLING_BASE_RUB,
  KW_EV_PREFERENTIAL_CEIL,
  KW_ICE_PREFERENTIAL_CEIL,
  type IceVolumeBlockKey,
  type ParagraphKey,
  type RevisionKey,
} from './recyclingReference';
import type { HybridUtilPower, ThirtyMinPower } from './thirtyMinutePower';

// === TYPES =============================================================

export interface RecyclingFeeInput {
  /** true — электромобиль или последовательный гибрид (пункты 1 / 3). */
  isEv: boolean;
  /** Мощность для тарифа, кВт, без округления (для EV — 30-минутная). */
  powerKw: number;
  /** Рабочий объём ДВС, см³ (для EV не используется). */
  volumeCc: number;
  /** true — до 3 лет (колонка «новые»), false — старше 3 лет. */
  isNew: boolean;
  buyerType: 'individual' | 'company';
  personalUse: boolean;
  /** Дата расчёта — выбирает редакцию справочника. Нет — сегодня. */
  date?: Date | string;
}

export interface RecyclingFeeResult {
  /** Сбор, ₽ (Math.round от базовой ставки × коэффициент). */
  fee: number;
  coeff: number;
  paragraph: ParagraphKey;
  /** Блок объёма ДВС; null у EV. */
  volumeBlock: IceVolumeBlockKey | null;
  revision: RevisionKey;
  /** true — сработала льгота физлица для личного пользования (0,17 / 0,26). */
  isPreferential: boolean;
  /** Ветка для текста: юрлицо / коммерческая ставка физлица / льготный. */
  buyerLabel: 'юрлицо' | 'коммерч.' | 'льготный';
  /** Мощность, ушедшая в поиск коэффициента, кВт. */
  powerKw: number;
}

// === CALCULATION =======================================================

/**
 * Recycling fee of one M1 vehicle, from the verified reference.
 * @input  RecyclingFeeInput (power already in kW; for EV already the 30-minute power)
 * @output RecyclingFeeResult
 * @important isPreferential compares the personal-use coefficient with the commercial one for the
 *            same bracket: the benefit is exactly "п.3/п.4 cheaper than п.1/п.2 here". The newest revision is
 *            open-ended in the JSON (validTo 9999-12-31), so a date after it still prices by it until the
 *            next revision is added — a data edit, not a code change.
 */
export function calcRecyclingFeeKw(input: RecyclingFeeInput): RecyclingFeeResult {
  const { isEv, powerKw, volumeCc, isNew, buyerType, personalUse } = input;
  const revision = resolveRevision(input.date ?? new Date());
  const age = isNew ? 'new' : 'over3';
  const volumeBlock = isEv ? null : volumeBlockForCc(volumeCc);
  const commercial: ParagraphKey = isEv
    ? 'paragraph_1_ev_series_hybrid_not_personal'
    : 'paragraph_2_ice_not_personal';
  const personal = buyerType === 'individual' && personalUse;
  const paragraph: ParagraphKey = !personal
    ? commercial
    : isEv
      ? 'paragraph_3_ev_series_hybrid_personal_use'
      : 'paragraph_4_ice_personal_use';

  const coeff = findCoefficientKw(revision, paragraph, volumeBlock, powerKw, age);
  const isPreferential = personal && coeff < findCoefficientKw(revision, commercial, volumeBlock, powerKw, age);
  const buyerLabel = buyerType === 'company' ? 'юрлицо' : isPreferential ? 'льготный' : 'коммерч.';

  return {
    fee: Math.round(RECYCLING_BASE_RUB * coeff),
    coeff,
    paragraph,
    volumeBlock,
    revision,
    isPreferential,
    buyerLabel,
    powerKw,
  };
}

// === BREAKDOWN TEXT ====================================================

const BLOCK_LABEL: Record<IceVolumeBlockKey, string> = {
  upTo1000: 'объём до 1.0 л',
  from1000to2000: 'объём 1.0–2.0 л',
  from2000to3000: 'объём 2.0–3.0 л',
  from3000to3500: 'объём 3.0–3.5 л',
  over3500: 'объём > 3.5 л',
};

/** Мощность в кВт для текста: до двух знаков после запятой, без округления в тарифе. */
function fmtKw(kw: number): string {
  return kw.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/** «30-мин …» part of the text: documented value, or the peak × 0,45 estimate labelled as such. */
function thirtyMinNote(t: ThirtyMinPower): string {
  return t.source === 'documents'
    ? `30-мин ${fmtKw(t.kw)} кВт (по документам)`
    : `30-мин ≈ ${fmtKw(t.kw)} кВт (оценка: пиковая × 0,45)`;
}

/**
 * Text of the «Утилизационный сбор» breakdown row.
 * @input  r — result of calcRecyclingFeeKw; thirtyMin — how the EV power was obtained (EV only);
 *         hybrid — ICE + motor parts of a hybrid's power (hybrid with known parts only)
 * @output e.g. «20 000 ₽ × 78 (коммерч., электро/посл. гибрид, до 3 лет, 30-мин ≈ 101,94 кВт
 *         (оценка: пиковая × 0,45))» or «20 000 ₽ × 131.04 (коммерч., объём 2.0–3.0 л, до 3 лет, 223,59 кВт)»
 *         or, for a hybrid, «… ДВС 77,96 кВт + мотор 30-мин ≈ 43,36 кВт (оценка: пиковая × 0,45) = 121,32 кВт»
 * @important Estimated EV / motor power is always labelled as an estimate (решения владельца В1, В4).
 */
export function recyclingDetails(
  r: RecyclingFeeResult,
  isNew: boolean,
  thirtyMin?: ThirtyMinPower,
  hybrid?: HybridUtilPower,
): string {
  const base = RECYCLING_BASE_RUB.toLocaleString('ru-RU');
  const ageLabel = isNew ? 'до 3 лет' : '3+ лет';
  const powerNote = thirtyMin
    ? thirtyMinNote(thirtyMin)
    : hybrid
      ? `ДВС ${fmtKw(hybrid.iceKw)} кВт + мотор ${thirtyMinNote(hybrid.motor)} = ${fmtKw(hybrid.totalKw)} кВт`
      : `${fmtKw(r.powerKw)} кВт`;
  if (r.isPreferential) {
    const ceiling = r.volumeBlock === null
      ? `≤${fmtKw(KW_EV_PREFERENTIAL_CEIL)} кВт`
      : `≤${fmtKw(KW_ICE_PREFERENTIAL_CEIL)} кВт и ≤3 л`;
    return `${base} ₽ × ${r.coeff} (льготный: ${ceiling}, ${ageLabel}, ${powerNote})`;
  }
  const category = r.volumeBlock === null ? 'электро/посл. гибрид' : BLOCK_LABEL[r.volumeBlock];
  return `${base} ₽ × ${r.coeff} (${r.buyerLabel}, ${category}, ${ageLabel}, ${powerNote})`;
}
